// Season Wrapped client orchestration.
//
// Ties the pieces together for the modal:
//   1. load the season's at-bats from Firestore
//   2. build the LOCKED SeasonWrappedData via the aggregator (template prose = floor)
//   3. call /api/season-wrapped for narration
//   4. merge the AI prose over the template copy (numbers never change)
//
// Step 2 alone yields a complete, renderable Wrapped. Steps 3-4 are a pure
// enhancement: on any failure we keep the template copy and the UI still works.

import { collection, getDocs } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { gamesForSeason } from '../utils';
import { apiUrl, postStreamedJson } from '../api';
import type { AtBatDoc, SeasonWrappedData, WrappedSlide } from '../../types/dugout';
import { buildSeasonWrapped } from './season-aggregator';
// Narration shapes come from the shared prompt module (also bundled into the
// Vercel function and the dev server) — the single source that can't drift.
import type { WrappedNarration, WrappedNarrationRequest } from '../../../api/_lib/insights/season-wrapped-prompt';

/** Derive the slim narration request from the full (locked) Wrapped data. */
function toNarrationRequest(d: SeasonWrappedData): WrappedNarrationRequest {
  return {
    teamName: d.teamName,
    ageGroup: d.ageGroup,
    season: d.season,
    seasonYear: d.seasonYear,
    record: d.record,
    runDiff: d.runDiff,
    gamesPlayed: d.gamesPlayed,
    biggestMoment: d.moments.biggestMoment
      ? { playerName: d.moments.biggestMoment.playerName, description: d.moments.biggestMoment.description }
      : null,
    clutchPlayer: d.moments.clutchPlayer
      ? { playerName: d.moments.clutchPlayer.playerName, statLine: d.moments.clutchPlayer.statLine }
      : null,
    topLeverageInning: d.moments.topLeverageInning
      ? { playerName: d.moments.topLeverageInning.playerName, description: d.moments.topLeverageInning.description }
      : null,
    superlatives: d.superlatives.map(s => ({
      playerId: s.playerId, playerName: s.playerName, title: s.title, statLine: s.statLine,
    })),
  };
}

/** Load every at-bat across the team's current-season games (tagged with gameId). */
async function loadSeasonAtBats(teamId: string, team: any): Promise<AtBatDoc[]> {
  const gamesSnap = await getDocs(collection(db, `teams/${teamId}/games`));
  const seasonGameIds = new Set(
    gamesForSeason(
      gamesSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) })),
      team?.season,
      team?.seasonYear,
      true,
    ).map((g: any) => g.id),
  );

  const all: AtBatDoc[] = [];
  await Promise.all(
    gamesSnap.docs
      .filter(g => seasonGameIds.has(g.id))
      .map(async g => {
        const abSnap = await getDocs(collection(db, `teams/${teamId}/games/${g.id}/atBats`));
        abSnap.forEach(d => all.push({ ...(d.data() as any), gameId: g.id }));
      }),
  );
  return all;
}

/** Build the locked Wrapped data (template copy only — no AI yet). */
export async function buildWrappedData(team: any, players: any[], allGames: any[]): Promise<SeasonWrappedData> {
  const seasonGames = gamesForSeason(allGames, team?.season, team?.seasonYear, true);
  const atBats = await loadSeasonAtBats(team.id, team);
  return buildSeasonWrapped({ team, players, games: seasonGames, atBats });
}

/** Re-thread AI prose into the data object AND the already-built slide list. */
function mergeNarration(data: SeasonWrappedData, n: WrappedNarration): SeasonWrappedData {
  const lineById = new Map((n.playerLines || []).map(l => [l.playerId, l.prose]));

  // Top-level prose.
  if (n.seasonThesis) data.thesis = n.seasonThesis;
  if (n.closingNote) data.closingNote = n.closingNote;

  // Moments.
  if (data.moments.biggestMoment && n.biggestMomentCall) {
    data.moments.biggestMoment.prose = n.biggestMomentCall;
  }
  if (data.moments.clutchPlayer && n.clutchProse) {
    data.moments.clutchPlayer.prose = n.clutchProse;
  }

  // Superlatives.
  data.superlatives.forEach(s => {
    const prose = lineById.get(s.playerId);
    if (prose) s.prose = prose;
  });

  // Re-thread into slides (they hold their own copies of these objects' values).
  data.slides = data.slides.map((slide: WrappedSlide): WrappedSlide => {
    switch (slide.kind) {
      case 'record':
        return { ...slide, thesis: data.thesis };
      case 'closing':
        return { ...slide, note: data.closingNote };
      case 'biggestMoment':
        return data.moments.biggestMoment ? { ...slide, moment: data.moments.biggestMoment } : slide;
      case 'clutchPlayer':
        return data.moments.clutchPlayer ? { ...slide, clutch: data.moments.clutchPlayer } : slide;
      case 'superlative': {
        const prose = lineById.get(slide.superlative.playerId);
        return prose ? { ...slide, superlative: { ...slide.superlative, prose } } : slide;
      }
      default:
        return slide;
    }
  });

  data.aiNarrated = true;
  return data;
}

/**
 * Generate a Wrapped end-to-end: build locked data, then attempt AI narration.
 * Always resolves to a complete SeasonWrappedData. `aiNarrated` reflects whether
 * the AI layer was applied (false = running on template copy, which is fine).
 */
export async function generateWrapped(team: any, players: any[], allGames: any[]): Promise<SeasonWrappedData> {
  const data = await buildWrappedData(team, players, allGames);

  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = await auth.currentUser?.getIdToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    const body = JSON.stringify(toNarrationRequest(data));
    const res = await postStreamedJson(apiUrl('/api/season-wrapped'), headers, body, 'Wrapped');
    if (res?.__httpError || res?.error) return data; // keep template floor
    return mergeNarration(data, res as WrappedNarration);
  } catch {
    return data; // any failure → template floor, still a complete Wrapped
  }
}
